import Vue from 'vue'

//  axios
import axios from 'axios'
import * as Sentry from '@sentry/vue'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import router from "@/router";

const csrfToken = window.Laravel.csrfToken || document.querySelector('meta[name="csrf-token"]').getAttribute('content')

const axiosIns = axios.create({
    baseURL: window.APP_URL || 'https://veltacar.com.br/',
})

// Endpoint sem query string e com ids numéricos trocados por ":id", para o
// fingerprint agrupar "/agendamento/123" e "/agendamento/456" no mesmo issue.
const normalizarEndpoint = url => String(url || '')
    .split('?')[0]
    .split('/')
    .map(segmento => (/^\d+$/.test(segmento) ? ':id' : segmento))
    .join('/')

// Separa os 5xx em três grupos no Sentry, para o volume de um não esconder o
// outro:
// - 520–526: a Cloudflare não alcançou a origem (servidor fora do ar). Um
//   issue só por incidente, não um por endpoint — a queda de 29/10/2026 abriu
//   mais de 30 issues de 522.
// - 500 com mensagem do próprio backend: recusa de negócio que o controller
//   devolve como 500 (ex.: "Orçamento não encontrado"). Vira warning, com a
//   mensagem anexada.
// - O resto (Laravel em produção responde só "Server Error"): exceção de fato.
const classificarFalhaDeServidor = (response, endpoint) => {
    const status = response.status
    if (status >= 520 && status <= 526) {
        return {
            level: 'warning',
            tags: { http_status: status, endpoint, falha_servidor: 'origem_inacessivel' },
            fingerprint: ['http-origem-inacessivel'],
        }
    }

    const mensagem = typeof response.data?.message === 'string' ? response.data.message : ''
    if (mensagem && mensagem !== 'Server Error') {
        return {
            level: 'warning',
            tags: { http_status: status, endpoint, falha_servidor: 'recusa_do_backend' },
            extra: { mensagem: mensagem.slice(0, 500) },
            fingerprint: ['http-5xx-recusa', endpoint],
        }
    }

    return {
        tags: { http_status: status, endpoint, falha_servidor: 'excecao' },
        fingerprint: ['http-5xx', endpoint],
    }
}

// Evita "spam" do aviso de rede quando várias requisições falham juntas
let networkErrorToastShown = false

axiosIns.interceptors.response.use(
    response => response,
    error => {
        const { config, response } = error
        const originalRequest = config

        // Erro de rede: a requisição não recebeu resposta do servidor (offline,
        // timeout, conexão móvel instável, requisição abortada). Mostra um aviso
        // amigável sem deslogar o usuário, evitando que a Promise rejeitada vire
        // ruído de "unhandledrejection". (Sentry VELTACAR-VUE-M)
        if (!response && !axios.isCancel(error)) {
            error.handled = true

            if (!networkErrorToastShown) {
                networkErrorToastShown = true
                setTimeout(() => { networkErrorToastShown = false }, 5000)

                Vue.$toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Sem conexão',
                        icon: 'WifiOffIcon',
                        text: 'Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.',
                        variant: 'warning',
                    },
                })
            }

            return Promise.reject(error)
        }

        if (response && response.status === 422 && !String(originalRequest.url).includes("auth")) {
            Vue.$toast({
                component: ToastificationContent,
                props: {
                    title: 'VOCÊ FOI DESLOGADO',
                    icon: 'BellIcon',
                    text: 'Acesse sua conta novamente para ter acesso!',
                    variant: 'danger',
                },
            })
            setTimeout(() => {
                // `sair=1`: aqui a sessão acabou de ser invalidada pelo backend,
                // então a saída é intencional. Sem a flag o guard de rota só
                // desloga por acidente (porque o `userData` ainda está na
                // memória 2s depois) e poderia até tentar restaurar a sessão.
                router.push({
                    name: 'login-geral',
                    query: { sair: '1' },
                }).catch(() => {})
            }, 2000)
        }

        if (response && response.status === 419 && !originalRequest._retry) {
            originalRequest._retry = true;

            return getCsrfToken().then(csrfToken => {
                window.Laravel.csrfToken = csrfToken
                document.querySelector('meta[name="csrf-token"]').setAttribute('content', csrfToken);
                originalRequest.headers['X-CSRF-TOKEN'] = csrfToken;

                return axios(originalRequest);
            });
        }

        // Interceptar erro 403 - Acesso Negado
        if (response && response.status === 403) {
            const errorMessage = response.data?.message || '';
            const currentPath = window.location.pathname;
            const isClientArea = currentPath.includes('/sistema/cliente');

            // Bloqueio por plano (PlanoCompletoMiddleware): redireciona pra tela
            // dedicada de upgrade. So aplica na area da empresa - rotas de cliente
            // nao devem cair em rotas de plano.
            const isPlanoBlock = !isClientArea && (
                errorMessage.includes('Plano Completo') ||
                errorMessage.includes('Faça upgrade') ||
                errorMessage.includes('Renove sua assinatura') ||
                errorMessage.includes('Nenhum plano encontrado')
            );

            if (isPlanoBlock) {
                error.handled = true;

                Vue.$toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Recurso bloqueado pelo seu plano',
                        icon: 'LockIcon',
                        text: errorMessage,
                        variant: 'warning',
                    },
                })

                if (router.currentRoute.name !== 'planoBloqueado') {
                    // Usa replace pra tirar do historico a tela bloqueada -
                    // assim o "Voltar" da tela de plano nao volta pra rota
                    // que disparou o 403 e cai em loop.
                    setTimeout(() => {
                        router.replace({
                            name: 'planoBloqueado',
                            query: { msg: errorMessage },
                        }).catch(() => {})
                    }, 1500)
                }

                return Promise.reject(error)
            }

            const isAccessDeniedError = errorMessage.includes('ACESSO NEGADO') ||
                                       errorMessage.includes('não possui permissão') ||
                                       response.data?.exception === 'Symfony\\Component\\HttpKernel\\Exception\\AccessDeniedHttpException';

            if (isAccessDeniedError) {
                error.handled = true;

                Vue.$toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Acesso Negado',
                        icon: 'AlertCircleIcon',
                        text: errorMessage || 'Você não possui permissão para acessar essa tela.',
                        variant: 'danger',
                    },
                })

                setTimeout(() => {
                    router.push({
                        name: isClientArea ? 'naoAutorizado-client' : 'naoAutorizado',
                    }).catch(() => {})
                }, 1500)
            }
        }

        // 5xx: reporta ao Sentry com o endpoint. Sem isso toda falha de servidor
        // caía num único issue "Request failed with status code 500" (via
        // unhandledrejection), sem dizer qual rota quebrou. VELTACAR-VUE-1
        // 501 fica de fora: é status de negócio ("sem plano ativo", "registro já
        // excluído") tratado pela tela, não falha de servidor.
        if (response && response.status >= 500 && response.status !== 501) {
            const endpoint = normalizarEndpoint(config?.url)
            error.reportadoAoSentry = true
            Sentry.captureException(error, classificarFalhaDeServidor(response, endpoint))
        }

        return Promise.reject(error)
    },
)

function getCsrfToken() {
    return axios.get('/api/csrf-token')
        .then(response => {
            return response.data.csrfToken;
        })
        .catch(error => {
            console.error('Erro ao obter token CSRF:', error);
            throw error;
        });
}


axiosIns.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken

Vue.prototype.$axios = axiosIns

export default axiosIns
